import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

/**
 * Lifecycle of the messaging SSE connection. Mirrors `InboxStreamStatus`
 * from `useInboxStream`. Tracked here (in addition to the hook's local
 * state) so the data-fetching layer can gate polling: when the stream
 * is `open`, server pushes drive every cache invalidation and polling
 * is wasted work; in any other state we want the slow poll fallback
 * so the inbox stays roughly fresh.
 */
export type SseStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

interface InboxState {
  totalUnread: number;
  setTotalUnread: (count: number) => void;
  incrementUnread: () => void;
  decrementUnread: (by?: number) => void;
  sseStatus: SseStatus;
  setSseStatus: (status: SseStatus) => void;
  /**
   * Thread ids where the AI agent (Iris) is currently composing a reply.
   * Driven by the `ai.generating` SSE event; the open thread renders a
   * typing indicator while its id is present. A Set (not an array) so the
   * membership test in the render path is O(1) and toggles are idempotent.
   */
  generatingThreadIds: Set<string>;
  setThreadGenerating: (threadId: string, generating: boolean) => void;
}

export const useInboxStore = create<InboxState>()(
  devtools(
    (set, get) => ({
      totalUnread: 0,

      setTotalUnread: (count) => {
        set({ totalUnread: count }, false, 'inbox/setTotalUnread');
      },

      incrementUnread: () => {
        set({ totalUnread: get().totalUnread + 1 }, false, 'inbox/incrementUnread');
      },

      decrementUnread: (by = 1) => {
        const next = Math.max(0, get().totalUnread - by);
        set({ totalUnread: next }, false, 'inbox/decrementUnread');
      },

      sseStatus: 'idle',
      setSseStatus: (status) => {
        if (get().sseStatus === status) return;
        set({ sseStatus: status }, false, 'inbox/setSseStatus');
      },

      generatingThreadIds: new Set<string>(),
      setThreadGenerating: (threadId, generating) => {
        const current = get().generatingThreadIds;
        if (generating === current.has(threadId)) return; // no-op — already in desired state
        // Replace the Set so selectors observing a specific thread re-run.
        const next = new Set(current);
        if (generating) next.add(threadId);
        else next.delete(threadId);
        set({ generatingThreadIds: next }, false, 'inbox/setThreadGenerating');
      },
    }),
    { name: 'InboxStore', enabled: import.meta.env.DEV },
  ),
);

export const useTotalUnread = () => useInboxStore((s) => s.totalUnread);
/**
 * Pure selector — returns true ONLY when SSE is established. Used by
 * the messaging react-query hooks to switch their polling off. Any
 * other status (idle / connecting / closed / error) keeps the slow
 * poll fallback alive so the inbox doesn't go silent on a transient
 * outage.
 */
export const useSseConnected = () => useInboxStore((s) => s.sseStatus === 'open');
/**
 * True while Iris is composing a reply for the given thread. Selects a boolean
 * so a subscriber only re-renders when THIS thread's generating state flips —
 * not when some other thread's does.
 */
export const useThreadGenerating = (threadId: string | null | undefined) =>
  useInboxStore((s) => (threadId ? s.generatingThreadIds.has(threadId) : false));
export const useInboxActions = () =>
  useInboxStore(
    useShallow((s) => ({
      setTotalUnread: s.setTotalUnread,
      incrementUnread: s.incrementUnread,
      decrementUnread: s.decrementUnread,
      setSseStatus: s.setSseStatus,
      setThreadGenerating: s.setThreadGenerating,
    })),
  );
